import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { AlertContext } from "../../../context/AlertContext";
import { ConfiguracionContext } from "../../../context/ConfiguracionContext";
import Spinner from "../Spinner";
import { AuthContext } from "../../../context/AuthContext";
import "../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressBar } from "primereact/progressbar";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { useNavigate } from "react-router-dom";
import Menu from "../Menu";
import clienteAxios from "../../../config/axios";

const formatoImporte = (valor) =>
  Number(valor).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatoFecha = (valor) => {
  if (!valor) return "";
  const [anio, mes, dia] = String(valor).substring(0, 10).split("-");
  return `${dia}/${mes}/${anio}`;
};

export default function ProcesosGenerarNc(props) {
  const [puntoVenta, setPuntoVenta] = useState(null);
  const [facturas, setFacturas] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [estado, setEstado] = useState(null);
  const toast = useRef(null);
  const intervalo = useRef(null);

  let navigate = useNavigate();

  //Context

  const alertContext = useContext(AlertContext);
  const { mensajeAlerta } = alertContext;

  const authContext = useContext(AuthContext);
  const { state } = authContext;

  const configuracionContext = useContext(ConfiguracionContext);
  const {
    statusAcceso,
    empresaElegida,
    empresas,
    puntosVentas,
    obtenerAcceso,
    obtenerEmpresas,
    obtenerPuntosVentas,
  } = configuracionContext;

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({
        usuarioId: state.usuario.id,
        layout: "ProcesoNC",
      });
    }
  }, [state.id]);

  useEffect(() => {
    obtenerEmpresas();
    return () => detenerConsulta();
  }, []);

  useEffect(() => {
    setFacturas(null);
    setEstado(null);
    setPuntoVenta(null);
    if (empresaElegida && empresaElegida.id) {
      obtenerPuntosVentas({ empresaId: empresaElegida.id });
      consultarEstado(true);
    }
  }, [empresaElegida]);

  const detenerConsulta = () => {
    if (intervalo.current) {
      clearInterval(intervalo.current);
      intervalo.current = null;
    }
  };

  // Consulta el avance del proceso en el back
  const consultarEstado = async (inicial = false) => {
    if (!empresaElegida) return;
    try {
      const respuesta = await clienteAxios.post(
        "/api/configuracion/estadonotascredito",
        { empresaId: empresaElegida.id }
      );
      const datos = respuesta.data;
      setEstado(datos);
      if (datos && datos.enCurso) {
        if (!intervalo.current) {
          intervalo.current = setInterval(() => consultarEstado(), 2000);
        }
      } else {
        detenerConsulta();
        if (!inicial && datos) {
          setFacturas(null);
        }
      }
    } catch (error) {
      console.log(error);
      detenerConsulta();
    }
  };

  const exitClick = () => {
    navigate("/");
  };

  const accesoDenegado = () => {
    mensajeAlerta(
      "Generar NC",
      "No tiene permiso para acceder a esta pantalla !.",
      "error"
    );
    exitClick();
    return;
  };

  const verFacturas = async () => {
    if (!empresaElegida) {
      mensajeAlerta("Generar NC", "Debe seleccionar una empresa !.", "error");
      return;
    }
    setCargando(true);
    try {
      const respuesta = await clienteAxios.post(
        "/api/configuracion/previsualizarnotascredito",
        { empresaId: empresaElegida.id }
      );
      setFacturas(respuesta.data);
      if (respuesta.data.length === 0) {
        toast.current.show({
          severity: "info",
          summary: "Generar NC",
          detail: "No hay facturas para acreditar.",
          life: 3000,
        });
      }
    } catch (error) {
      console.log(error);
      mensajeAlerta(
        "Generar NC",
        "Hubo un error al obtener las facturas !.",
        "error"
      );
    }
    setCargando(false);
  };

  const iniciarProceso = async () => {
    try {
      await clienteAxios.post("/api/configuracion/generarnotascredito", {
        empresaId: empresaElegida.id,
        puntoVenta: puntoVenta.numeroPv,
        usuario: state.usuario ? state.usuario.usuario : null,
      });
      consultarEstado();
    } catch (error) {
      console.log(error);
      mensajeAlerta(
        "Generar NC",
        error.response && error.response.data
          ? error.response.data
          : "Hubo un error al iniciar el proceso !.",
        "error"
      );
    }
  };

  const generarClick = () => {
    if (!puntoVenta) {
      mensajeAlerta("Generar NC", "Debe seleccionar el punto de venta !.", "error");
      return;
    }
    if (!facturas || facturas.length === 0) {
      mensajeAlerta("Generar NC", "No hay facturas para acreditar !.", "error");
      return;
    }
    confirmDialog({
      message: `Se van a emitir ${facturas.length} notas de crédito por un total de $ ${formatoImporte(
        totalFacturas
      )} en ${empresaElegida.nombre}, punto de venta ${
        puntoVenta.numeroPv
      }. ¿Desea continuar?`,
      header: "Generar Notas de Crédito",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      closable: false,
      acceptLabel: "Si",
      accept: () => iniciarProceso(),
    });
  };

  const totalFacturas = facturas
    ? facturas.reduce((acc, item) => acc + Number(item.saldo), 0)
    : 0;

  const enCurso = estado && estado.enCurso;
  const porcentaje =
    estado && estado.total > 0
      ? Math.round((estado.procesadas / estado.total) * 100)
      : 0;

  const empresa =
    empresas && empresaElegida
      ? empresas.find((item) => item.id === empresaElegida.id)
      : null;

  return (
    <Fragment>
      <Menu />
      <Toast ref={toast} />
      <ConfirmDialog />
      {statusAcceso === 0 ? <Spinner /> : null}
      {statusAcceso === 400 ? accesoDenegado() : null}
      {statusAcceso === 200 ? (
        <div style={{ padding: "1.5vw" }}>
          <div style={{ marginBottom: "1vw" }}>
            <Tag
              value={
                "Generar Notas de Crédito" +
                (empresaElegida ? " - " + empresaElegida.nombre : "")
              }
            ></Tag>
            {empresa && empresa.facturaElectronica ? (
              <Tag
                style={{ marginLeft: "1vw" }}
                value="Factura Electrónica"
                severity="danger"
              ></Tag>
            ) : null}
          </div>

          <p style={{ fontSize: "small", color: "#555", marginTop: 0 }}>
            Acredita el saldo de las facturas de los últimos 4 meses de
            titulares suspendidos hasta 2030 o dados de baja (salvo motivo 126)
            que todavía no tienen nota de crédito.
          </p>

          <Grid container spacing={3} alignItems="flex-end">
            <Grid item xs={12} md={2}>
              <Autocomplete
                id="puntoVenta"
                fullWidth
                disabled={enCurso}
                value={puntoVenta}
                options={
                  puntosVentas && empresaElegida
                    ? puntosVentas.filter(
                        (item) =>
                          item.habilitado &&
                          item.id_empresa === empresaElegida.id
                      )
                    : []
                }
                getOptionLabel={(option) => String(option.numeroPv)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="standard"
                    label="P. Venta *"
                    size="small"
                  />
                )}
                onChange={(event, value) => setPuntoVenta(value || null)}
              />
            </Grid>
            <Grid item xs={12} md={10}>
              <Button
                icon="pi pi-search"
                label="Ver facturas"
                className="p-button-raised"
                onClick={verFacturas}
                disabled={enCurso || cargando}
                loading={cargando}
                style={{ marginRight: "1em" }}
              />
              <Button
                icon="pi pi-file-edit"
                label="Generar NC"
                className="p-button-raised p-button-danger"
                onClick={generarClick}
                disabled={enCurso || !facturas || facturas.length === 0}
                style={{ marginRight: "1em" }}
              />
              <Button
                icon="pi pi-times"
                label="Salir"
                className="p-button-raised p-button-secondary"
                onClick={exitClick}
              />
            </Grid>
          </Grid>

          {estado ? (
            <div style={{ marginTop: "1.5vw" }}>
              {enCurso ? (
                <Fragment>
                  <p>
                    Procesando {estado.procesadas} de {estado.total}...
                  </p>
                  <ProgressBar value={porcentaje}></ProgressBar>
                </Fragment>
              ) : (
                <p>
                  <b>Último proceso:</b> {estado.mensaje} Emitidas:{" "}
                  {estado.emitidas.length} - Con error: {estado.errores.length}
                </p>
              )}

              {estado.errores.length > 0 ? (
                <DataTable
                  value={estado.errores}
                  size="small"
                  header="Errores"
                  style={{ marginTop: "1vw" }}
                  className="p-datatable-sm"
                >
                  <Column field="factura" header="Factura"></Column>
                  <Column field="error" header="Error"></Column>
                </DataTable>
              ) : null}

              {estado.emitidas.length > 0 ? (
                <DataTable
                  value={estado.emitidas}
                  size="small"
                  header="Notas de crédito emitidas"
                  paginator
                  paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                  currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  rows={100}
                  style={{ marginTop: "1vw" }}
                  className="p-datatable-sm"
                >
                  <Column field="factura" header="Factura"></Column>
                  <Column field="nc" header="Nota de Crédito"></Column>
                  <Column field="titular" header="Titular"></Column>
                  <Column field="nombre" header="Nombre"></Column>
                  <Column
                    field="total"
                    header="Total"
                    body={(row) => formatoImporte(row.total)}
                    style={{ textAlign: "right" }}
                  ></Column>
                  <Column field="cae" header="CAE"></Column>
                </DataTable>
              ) : null}
            </div>
          ) : null}

          {facturas && facturas.length > 0 && !enCurso ? (
            <DataTable
              value={facturas}
              size="small"
              header={`Facturas a acreditar: ${facturas.length} - Total $ ${formatoImporte(
                totalFacturas
              )}`}
              paginator
              paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
              currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
              rowsPerPageOptions={[10, 20, 50, 100]}
              rows={100}
              style={{ marginTop: "1.5vw" }}
              className="p-datatable-sm"
            >
              <Column
                field="fecha"
                header="Fecha"
                body={(row) => formatoFecha(row.fecha)}
              ></Column>
              <Column
                header="Factura"
                body={(row) =>
                  `${row.comprobante}-${row.puntoventa}-${row.numero}`
                }
              ></Column>
              <Column field="periodo" header="Período"></Column>
              <Column field="id_titular" header="Titular"></Column>
              <Column field="nombre" header="Nombre"></Column>
              <Column field="motivo" header="Motivo"></Column>
              <Column field="nc" header="Genera"></Column>
              <Column
                field="saldo"
                header="Saldo"
                body={(row) => formatoImporte(row.saldo)}
                style={{ textAlign: "right" }}
              ></Column>
            </DataTable>
          ) : null}
        </div>
      ) : null}
    </Fragment>
  );
}
